import React, { useEffect, useRef } from 'react';

import Cookie from 'js-cookie';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import Validate from 'validate.js';

import { COOKIE_DEV_MODE } from '../../../../../../../constants/Common';

import AuthService from '../../../../../../../services/Api/Auth';

import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../../hooks/connectAuth';

import Input from '../../../../Common/Form/Input/Input';
import InputIcon, { IInputIconWithType } from '../../../../Common/Form/Input/InputIcon';
import { IAuthState } from '../../../Store/Reducers';
import { Types, initialState as reducerInitialState } from '../../../Store/SignUpReducer';


interface IInputEmail {
    email: string,
    error: string,
    reset: boolean,
    loading: boolean,
    touched: boolean,
    sugestion: string,
    customPlaceholder?: string,
}

type TInputEmail = IConnectedProps<IInputEmail>;

interface IInputEmailIcon extends IInputIconWithType {
    loading: boolean
}

const InputEmailIcon: React.FC<IInputEmailIcon> = ({ loading, ...props }) => (
    <InputIcon {...props} width={24} height={24} loading={!!loading} data-cy="input-auth-email-icon" />
);

InputEmailIcon.defaultProps = {
    __TYPE: 'InputTrailingIcon'
};

const InputContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space.bee};
`;

const Suggestion = styled.span`
    display: flex;
    cursor: pointer;
    align-items: center;
    color: ${({ theme }) => theme.colors.gray700};
    font-size: ${({ theme }) => theme.fontSizes.mouse};
`;

const initialState = reducerInitialState.SignUp.personalData.email;

const InputEmail: React.FC<TInputEmail> = ({ intl, dispatch, ...props }) => {
    const input = useRef<HTMLInputElement>(null);

    const $AuthService = new AuthService();

    const { email, error, reset, touched, sugestion, loading, customPlaceholder } = props;

    const placeholder = customPlaceholder || intl.formatMessage({ id: 'authFunctional.personalData.placeholders.email' });

    const handleChangeEmail = ({ target }: React.ChangeEvent<HTMLInputElement>) => dispatch({
        type: Types.CHANGE_PERSONAL_FIELD_DATA,
        field: 'email',
        data: {
            error: '',
            sugestion: '',
            value: target.value.toLowerCase()
        }
    });

    const handleClickSugestion = () => {
        dispatch({
            type: Types.CHANGE_PERSONAL_FIELD_DATA,
            field: 'email',
            data: {
                value: sugestion,
                sugestion: ''
            }
        });

        if (!input.current) return;

        input.current.focus();
    };

    const apiMailValidate = async () => {
        if (Cookie.get(COOKIE_DEV_MODE)) {
            dispatch({
                type: Types.CHANGE_PERSONAL_FIELD_DATA,
                field: 'email',
                data: { loading: false }
            });

            return;
        }

        if (loading || !touched || email.length < 6) return;

        dispatch({
            type: Types.CHANGE_PERSONAL_FIELD_DATA,
            field: 'email',
            data: { loading: true }
        });

        try {
            const { validationKey, correctEmail } = await $AuthService.emailValidation(email);

            if (validationKey && !correctEmail) {
                dispatch({
                    type: Types.CHANGE_PERSONAL_FIELD_DATA,
                    field: 'email',
                    data: {
                        loading: false,
                        error: intl.formatMessage({ id: 'authFunctional.errors.invalidEmailFormatted' })
                    }
                });

                return;
            }

            if (correctEmail) {
                dispatch({
                    type: Types.CHANGE_PERSONAL_FIELD_DATA,
                    field: 'email',
                    data: {
                        loading: false,
                        sugestion: correctEmail
                    }
                });

                return;
            }

            dispatch({
                type: Types.CHANGE_PERSONAL_FIELD_DATA,
                field: 'email',
                data: { loading: false }
            });
        } catch (err) {
            dispatch({
                type: Types.CHANGE_PERSONAL_FIELD_DATA,
                field: 'email',
                data: {
                    loading: false,
                    error: intl.formatMessage({ id: 'authFunctional.errors.invalidEmailFormatted' })
                }
            });
        }
    };

    const handleBlurEmail = () => {
        if (!touched) return;

        const emailRule = {
            presence: {
                message: intl.formatMessage({ id: 'authFunctional.errors.emailEmpty' })
            },
            email: {
                message: intl.formatMessage({ id: 'authFunctional.errors.invalidEmail' })
            }
        };

        const isValid = Validate.single(email, emailRule);

        if (isValid) {
            dispatch({
                type: Types.CHANGE_PERSONAL_FIELD_DATA,
                field: 'email',
                data: { error: isValid[0] }
            });

            return;
        }

        if (!Cookie.get(COOKIE_DEV_MODE) && !sugestion) {
            apiMailValidate();
        }
    };

    useEffect(() => {
        if (!reset) return;

        dispatch({
            type: Types.CHANGE_PERSONAL_FIELD_DATA,
            field: 'email',
            data: { initialState }
        });
    }, [reset, dispatch]);

    return (
        <InputContainer>
            <Input
                order={7}
                ref={input}
                type="email"
                value={email}
                error={error}
                id="auth-email"
                onBlur={handleBlurEmail}
                placeholder={placeholder}
                onChange={handleChangeEmail}
            >
                <InputEmailIcon loading={loading} />
            </Input>

            {sugestion && (
                <Suggestion>
                    <FormattedMessage id="authFunctional.personalData.emailSugestion" />
                    <u onClick={handleClickSugestion}>...{sugestion}?</u>
                </Suggestion>
            )}
        </InputContainer>
    );
};

const mapStateToProps: IAuthMapStateToProps<IInputEmail> = (state, isComparison = false) => ((!isComparison)
    ? {
        reset: (state as IAuthState).Container.reset,
        error: (state as IAuthState).SignUp.personalData.email.error,
        email: (state as IAuthState).SignUp.personalData.email.value,
        touched: (state as IAuthState).SignUp.personalData.email.touched,
        loading: (state as IAuthState).SignUp.personalData.email.loading,
        sugestion: (state as IAuthState).SignUp.personalData.email.sugestion,
        customPlaceholder: (state as IAuthState).Container.communication.placeholderEmail
    } :
    {
        reset: state.reset,
        error: state.error,
        email: state.email,
        touched: state.touched,
        loading: state.loading,
        sugestion: state.sugestion,
        customPlaceholder: state.customPlaceholder
    }
);

export default connectAuth(mapStateToProps)(InputEmail);
